/**
 * AUTO-UX / AUTO-1/2 — Automations primary (publish a rule · not an Incident city).
 * Soft≠Live: reuses AutomationsDesk Live buses; Compat Rules = legacy hash only.
 * Cross-primary Mission Room uses leave-seat confirm — no default chip.
 */
import { useEffect, useState } from "react";
import { html, Alert, stay } from "./ui.js";
import { AutomationsDesk } from "./automations-desk.js";
import { Rules } from "./response-rules.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { CAP_HOME } from "./cmc-entry-registry.js";
import { useApi } from "./api.js";
import { autonomyPlanesOf, fourVisiblesOf } from "./autonomy-planes.js";
import { operatorSay, whySay } from "./operator-say.js";
import { ObjectCard, WorkbenchHead, PrepSpine } from "./object-workbench.js";

const SUBS = new Set(["library", "runs", "exceptions", "safety", "legacy-rules", ""]);
const FLOOR_TABS = [
  { id: "library", label: "Library", say: "Write the rule" },
  { id: "runs", label: "Runs", say: "What fired" },
  { id: "safety", label: "Safety", say: "What may publish" },
];
const HASH_ONLY = [
  { id: "exceptions", label: "Exceptions" },
  { id: "legacy-rules", label: "Compat rules" },
];

export default function AutomationsPrimary({ sub, toast }) {
  const routePane = SUBS.has(String(sub || "")) ? String(sub || "library") : "library";
  const [pane, setPane] = useState(routePane);
  useEffect(() => setPane(routePane), [routePane]);
  const select = (id) => {
    setPane(id);
    stay("/automations/" + id);
  };
  const leaveMission = () => confirmLeaveSeat({
    fromRoute: "/automations/" + pane,
    targetPath: CAP_HOME.needs_you.path,
    objectLabel: "Mission Room",
  });

  const home = useApi("/ai/home-context");
  const planes = autonomyPlanesOf(home.data && home.data.autonomy);
  const four = fourVisiblesOf(planes, { loading: !!(home.loading && !home.data) });
  const cutover = useApi("/ai/gateway/cutover-status");
  const cut = cutover.data && typeof cutover.data === "object" ? cutover.data : null;
  const denied = /403|401/.test(String(cutover.error || ""));
  const homeReady = !!(home.data && home.data.autonomy);
  const autoFour = cut && !denied
    ? {
        desired: String((cut.desired_families || []).length) + "/" + String((cut.effective_families || []).length),
        rev: (four.rev && four.rev !== "—" && four.rev !== "…" ? four.rev + "·" : "")
          + (cut.flag_revision != null ? ("f" + String(cut.flag_revision)) : "—"),
        ack: cut.flag_ack_expected
          ? String(cut.flag_ack_n || 0) + "/" + String(cut.flag_ack_expected) + (cut.flag_lag ? " lag" : "")
          : four.ack,
        why: four.why && four.why !== "—" ? four.why : (cut.families_ack ? "ready" : "families mismatch"),
      }
    : homeReady
      ? four
      : {
          desired: denied ? "seat" : four.desired,
          rev: four.rev,
          ack: denied ? "hidden" : four.ack,
          why: home.loading ? "…" : (denied ? "global rails not this seat" : four.why),
        };

  return html`<div className="sheet automations-primary desk-craft house-rack house-ia obj-wb"
      data-cmc-auto="auto-1"
      data-cmc-shell="auto-ux"
      data-cmc-obj-wb="1091"
      data-cmc-audience="tenant"
      data-cmc-four-visibles="tenant-tool-plane"
      data-cmc-auto2=${pane === "safety" ? "auto-2" : ""}
      data-cmc-compat=${pane === "legacy-rules" ? "1" : "0"}>
    <${WorkbenchHead}
      heading="h1"
      eyebrow="Automations · rule object"
      title="Publish a rule — not an Incident city"
      say="Write, practice, then publish. Incidents stay in Response. Simulation never invents an outbox."
      object=${html`<${ObjectCard} kind="Automation" title=${(FLOOR_TABS.find((t) => t.id === pane) || { label: pane || "Library" }).label}
        meta=${"Gap → next publish · verified receipts only"}
        status=${{ on: pane === "runs", label: pane === "runs" ? "Verify runs" : "Prepare" }}/>`}
    />
    <${PrepSpine}
      label="Automation preparation"
      activeId=${pane === "legacy-rules" || pane === "exceptions" ? "library" : (pane || "library")}
      onPick=${select}
      steps=${FLOOR_TABS.map((t) => ({
        id: t.id,
        label: t.label,
        say: t.say,
        done: FLOOR_TABS.findIndex((x) => x.id === pane) > FLOOR_TABS.findIndex((x) => x.id === t.id),
      }))}
    />
    <div className="ops-cockpit__metrics house-ia__meters" data-cmc-four-visibles="1" data-cmc-audience="tenant-tool-plane">
      <div className="ops-cockpit__m" data-cmc-desired-effective="desired vs effective"><b>${operatorSay(autoFour.desired, autoFour.desired || "—")}</b><span>How it should run</span></div>
      <div className="ops-cockpit__m"><b>${autoFour.rev}</b><span>Rule version</span></div>
      <div className="ops-cockpit__m" data-cmc-ack-lag="ACK / lag"><b>${autoFour.ack}</b><span>Boards answered</span></div>
      <div className="ops-cockpit__m"><b>${whySay(autoFour.why)}</b><span>Why blocked</span></div>
    </div>
    ${HASH_ONLY.some((t) => pane === t.id) ? html`<div className="chips automations-v3__tabs house-ia__tabs">
      ${HASH_ONLY.filter((t) => pane === t.id).map((t) => html`<button key=${t.id} type="button" className="chip"
        data-tone="go"
        onClick=${() => select(t.id)}>${t.label}</button>`)}
    </div>` : null}
    <p className="house-ia__aside">
      <button type="button" className="house-ia__aside-link" onClick=${leaveMission}>
        Incidents stay in Response
      </button>
    </p>
    ${pane === "exceptions"
      ? html`<${Alert} tone="warn" title="Exceptions"
          say="Failed or stopped runs surface here when the site returns them. Empty stays empty."/>`
      : null}
    ${pane === "runs"
      ? html`<${Alert} tone="info" title="Runs"
          say="Select a library rule below to load run history. Empty stays empty until the site returns rows."/>`
      : null}
    ${pane === "safety"
      ? html`<${Alert} tone="warn" title="Safety gates"
          say="Practice preview, blast preview, and publish require confirm drawers. Enable and Trigger stay propose-then-confirm. Simulation results are never invented here."/>`
      : null}
    ${pane === "legacy-rules"
      ? html`
          <${Alert} tone="warn" title="Compatibility response rules"
            say="Older dispatch rules stay here until the Watch proves the new library. Empty success is never invented."/>
          <${Rules} toast=${toast}/>`
      : html`<${AutomationsDesk} toast=${toast} focusPane=${pane} safetyMode=${pane === "safety"}/>`}
  </div>`;
}
